import type { DateRelation } from '@frontend/api';
import { DATE_RELATIONS } from '@frontend/api';
import {
  FormControl,
  FormErrorMessage,
  InputGroup,
  InputLeftAddon,
  VStack,
} from '@frontend/legacy-components';

import { capitalize } from 'utils/stringUtils';

import { valueUsesAmountAndUnitInput } from './DateRelationFilter.util';
import IsInclusiveCheckbox from './IsInclusiveCheckbox';
import MainDateAndTimeInputs from './MainDateAndTimeInputs';
import RelationSelector from './RelationSelector';
import { DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP } from './const/dateUnitPluralToDefaultAmountMap.const';
import { TimeUnitPlural, TimeUnitSingular } from './types';
import type {
  ExcludableDateRelation,
  RelationFilterValue,
  RelationFilterWorkingValue,
  RelationFilterWorkingValueByRelation,
} from './types';

const DEFAULT_DATE_UNIT_PLURAL = TimeUnitPlural.DAYS;

const DEFAULT_DATE_UNIT_SINGULAR = TimeUnitSingular.DAY;

const DEFAULT_IS_INCLUSIVE_VALUE = false;

const RELATION_TO_INITIAL_VALUE_MAP: {
  [Relation in DateRelation]: RelationFilterWorkingValueByRelation<Relation>;
} = {
  [DATE_RELATIONS.PREVIOUS]: {
    relation: DATE_RELATIONS.PREVIOUS,
    amount: DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP[DEFAULT_DATE_UNIT_PLURAL],
    unit: DEFAULT_DATE_UNIT_PLURAL,
    isInclusive: DEFAULT_IS_INCLUSIVE_VALUE,
  },
  [DATE_RELATIONS.NEXT]: {
    relation: DATE_RELATIONS.NEXT,
    amount: DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP[DEFAULT_DATE_UNIT_PLURAL],
    unit: DEFAULT_DATE_UNIT_PLURAL,
    isInclusive: DEFAULT_IS_INCLUSIVE_VALUE,
  },
  [DATE_RELATIONS.CURRENT]: {
    relation: DATE_RELATIONS.CURRENT,
    unit: DEFAULT_DATE_UNIT_SINGULAR,
  },
  [DATE_RELATIONS.BEFORE]: {
    relation: DATE_RELATIONS.BEFORE,
    startDate: null,
  },
  [DATE_RELATIONS.AFTER]: {
    relation: DATE_RELATIONS.AFTER,
    startDate: null,
  },
  [DATE_RELATIONS.ON]: {
    relation: DATE_RELATIONS.ON,
    startDate: null,
  },
  [DATE_RELATIONS.BETWEEN]: {
    relation: DATE_RELATIONS.BETWEEN,
    startDate: null,
    endDate: null,
  },
};

const ALL_OPTIONS: {
  value: DateRelation;
  label: string;
}[] = Object.values(DATE_RELATIONS).map((dateRelation) => ({
  value: dateRelation,
  label: capitalize(dateRelation),
}));

type WithExcludedRelationsProp<
  BaseType,
  ExcludedRelations extends ExcludableDateRelation,
> = [ExcludedRelations] extends [never]
  ? BaseType & {
      excludedRelations?: { [k in ExcludableDateRelation]?: false };
    }
  : BaseType & {
      excludedRelations: {
        [k in Exclude<ExcludableDateRelation, ExcludedRelations>]?: false;
      } & {
        [k in ExcludedRelations]: true;
      };
    };

export type ListTableRelationFilterProps<
  ExcludedRelations extends ExcludableDateRelation,
> = WithExcludedRelationsProp<
  {
    name?: string;
    value: RelationFilterValue<ExcludedRelations>;
    onChange: (newValue: RelationFilterWorkingValue<ExcludedRelations>) => void;
    error?: string | null;
  },
  ExcludedRelations
>;

const ListTableRelationFilter = <
  ExcludedRelations extends ExcludableDateRelation,
>({
  excludedRelations,
  name = 'dateRelationFilter',
  value,
  onChange,
  error,
}: ListTableRelationFilterProps<ExcludedRelations>) => {
  type NonExcludedRelation = Exclude<DateRelation, ExcludedRelations>;

  const defaultRelation = DATE_RELATIONS.ON as NonExcludedRelation;

  const getInitialValueByRelation = <Relation extends NonExcludedRelation>(
    relation: Relation,
  ): RelationFilterWorkingValueByRelation<Relation, ExcludedRelations> =>
    RELATION_TO_INITIAL_VALUE_MAP[
      relation
    ] as unknown as RelationFilterWorkingValueByRelation<
      Relation,
      ExcludedRelations
    >;

  const workingValue: RelationFilterWorkingValue<ExcludedRelations> =
    value ?? getInitialValueByRelation(defaultRelation);

  type RelationSelectorOption = {
    value: NonExcludedRelation;
    label: string;
  };

  const isExcludedRelation = (
    relation: DateRelation,
  ): relation is ExcludedRelations =>
    Object.keys(excludedRelations ?? {}).includes(relation);

  const relationSelectorOptions: RelationSelectorOption[] = ALL_OPTIONS.filter(
    (option) => !isExcludedRelation(option.value),
  ) as RelationSelectorOption[];

  const findRelationSelectorOptionByValue = (
    value: RelationSelectorOption['value'],
  ): RelationSelectorOption | undefined =>
    relationSelectorOptions.find((option) => option.value === value);

  const relationSelectorDefaultValue = findRelationSelectorOptionByValue(
    defaultRelation,
  ) as RelationSelectorOption;

  return (
    <FormControl as={VStack} w="100%" alignItems="flex-start">
      <InputGroup alignItems="center">
        <InputLeftAddon flexBasis="120px" flexShrink={0} p={0} borderWidth={0}>
          <RelationSelector
            name={name}
            options={relationSelectorOptions}
            value={findRelationSelectorOptionByValue(workingValue.relation)}
            defaultValue={relationSelectorDefaultValue}
            onChange={(selectedOption) => {
              const selectedRelation = (
                selectedOption ?? relationSelectorDefaultValue
              ).value;

              onChange(getInitialValueByRelation(selectedRelation));
            }}
          />
        </InputLeftAddon>
        <MainDateAndTimeInputs
          relationFilterValue={workingValue}
          onChange={onChange}
        />
      </InputGroup>
      {valueUsesAmountAndUnitInput(workingValue) ? (
        <IsInclusiveCheckbox
          isChecked={workingValue.isInclusive}
          onChange={(event) =>
            onChange({
              ...workingValue,
              isInclusive: event.target.checked,
            })
          }
          unit={workingValue.unit}
        />
      ) : null}
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

export default ListTableRelationFilter;
